import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Plus, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { contactsApi, documentsApi, ledgerApi, mydataError, DOC_GROUPS, DOC_STATUSES, VAT_RATES, PAYMENT_METHODS, BUY_TYPES, FINANCIAL_TYPES } from "@/api";
import { useLang, fmtMoney } from "@/i18n";
import { AccountPicker } from "@/components/AccountPicker";

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const newItem = () => ({ description: "", quantity: 1, unit_price: 0, vat_rate: 24, vat_deductible: true, account_code: "" });

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
    {children}
  </div>
);

export default function DocumentForm() {
  const { t } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [suggested, setSuggested] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    type: BUY_TYPES.includes(search.get("type")) || DOC_GROUPS.sales.includes(search.get("type")) ? search.get("type") : "invoice", contact_id: "", issue_date: today(), due_date: addDays(today(), 30),
    payment_method: "bank", status: "draft", items: [newItem()], notes: "",
  });
  const isBuy = BUY_TYPES.includes(form.type);
  const isFinancial = FINANCIAL_TYPES.includes(form.type);

  useEffect(() => { ledgerApi.expenseAccounts(isBuy ? "expense" : "income").then(setAccounts).catch(() => {}); }, [isBuy]);

  useEffect(() => {
    contactsApi.list().then(setContacts);
    if (id) documentsApi.get(id).then((d) => setForm({ ...d, due_date: d.due_date || "" })).catch(() => toast.error(t("error")));
  }, [id, t]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const setItem = (i, k) => (v) => setForm((f) => {
    const items = f.items.map((it, idx) => (idx === i ? { ...it, [k]: v?.target ? v.target.value : v } : it));
    return { ...f, items };
  });

  const onContact = async (cid) => {
    const c = contacts.find((x) => x.id === cid);
    setForm((f) => ({ ...f, contact_id: cid, due_date: c ? addDays(f.issue_date, c.payment_terms) : f.due_date }));
    try {
      const r = await documentsApi.lastAccount(cid, isBuy ? "purchases" : "sales");
      if (r.account_code) {
        setSuggested(r.account_code);
        setForm((f) => ({ ...f, items: f.items.map((it) => (it.account_code ? it : { ...it, account_code: r.account_code })) }));
        toast.info(`${t("account_suggested")} ${r.account_code} (${r.document_number})`);
      }
    } catch { /* no suggestion */ }
  };
  const addLine = () => setForm((f) => ({ ...f, items: [...f.items, { ...newItem(), account_code: suggested }] }));

  const totals = useMemo(() => {
    let subtotal = 0, vat = 0; const breakdown = {};
    form.items.forEach((it) => {
      const net = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
      const v = net * (Number(it.vat_rate) || 0) / 100;
      subtotal += net; vat += v;
      breakdown[it.vat_rate] = (breakdown[it.vat_rate] || 0) + v;
    });
    return { subtotal, vat, total: subtotal + vat, breakdown };
  }, [form.items]);

  const visibleContacts = contacts.filter((c) => isBuy ? c.type !== "customer" : c.type !== "supplier");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_id) return toast.error(`${t("contact")}: ${t("required")}`);
    const items = form.items.filter((it) => it.description.trim()).map((it) => ({
      description: it.description, quantity: Number(it.quantity) || 0, unit_price: Number(it.unit_price) || 0, vat_rate: Number(it.vat_rate) || 0,
      vat_deductible: isBuy ? it.vat_deductible !== false : true,
      account_code: it.account_code || "",
    }));
    if (!items.length) return toast.error(t("items_required"));
    if (isFinancial && form.status !== "draft" && form.status !== "cancelled" && items.some((it) => !it.account_code)) return toast.error(t("err_account_required"));
    setSaving(true);
    try {
      const payload = { type: form.type, contact_id: form.contact_id, issue_date: form.issue_date, due_date: form.due_date || null,
        payment_method: form.payment_method, status: form.status, items, notes: form.notes, supplier_number: isBuy ? form.supplier_number || "" : "" };
      const saved = id ? await documentsApi.update(id, payload) : await documentsApi.create(payload);
      toast.success(t("doc_saved"));
      navigate(`/documents/${saved.id}`);
    } catch (err) { toast.error(mydataError(err, t)); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6 page-enter max-w-6xl" data-testid="document-form">
      <div className="flex items-center gap-3">
        <Button type="button" variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="document-form-back-button"><ArrowLeft size={18} /></Button>
        <h1 className="text-2xl font-heading font-extrabold tracking-tight">{id ? t("edit_document") : t("new_document")}</h1>
      </div>

      <div className="erp-card p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label={t("type")}>
          <Select value={form.type} onValueChange={set("type")}>
            <SelectTrigger data-testid="document-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel className="text-blue-800 dark:text-blue-300">{t("group_sales")}</SelectLabel>
                {DOC_GROUPS.sales.map((v) => <SelectItem key={v} value={v}>{t(`doc_${v}`)}</SelectItem>)}
              </SelectGroup>
              <SelectSeparator />
              <SelectGroup>
                <SelectLabel className="text-orange-700 dark:text-orange-300">{t("group_purchases")}</SelectLabel>
                {DOC_GROUPS.purchases.map((v) => <SelectItem key={v} value={v}>{t(`doc_${v}`)}</SelectItem>)}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
        <Field label={t("contact")}>
          <Select value={form.contact_id} onValueChange={onContact}>
            <SelectTrigger data-testid="document-customer-select"><SelectValue placeholder={t("select_contact")} /></SelectTrigger>
            <SelectContent>{visibleContacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label={t("status")}>
          <Select value={form.status} onValueChange={set("status")}>
            <SelectTrigger data-testid="document-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{DOC_STATUSES.map((v) => <SelectItem key={v} value={v}>{t(`status_${v}`)}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label={t("issue_date")}>
          <DateInput value={form.issue_date} onChange={set("issue_date")} className="font-mono" data-testid="document-issue-date-input" />
        </Field>
        {isBuy && (
          <Field label={t("supplier_number")}>
            <Input value={form.supplier_number || ""} onChange={set("supplier_number")} placeholder={t("supplier_number_ph")} className="font-mono" data-testid="document-supplier-number-input" />
          </Field>
        )}
        <Field label={t("due_date")}>
          <DateInput value={form.due_date || ""} onChange={set("due_date")} className="font-mono" data-testid="document-due-date-input" />
        </Field>
        <Field label={t("payment_method")}>
          <Select value={form.payment_method} onValueChange={set("payment_method")}>
            <SelectTrigger data-testid="document-payment-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PAYMENT_METHODS.map((v) => <SelectItem key={v} value={v}>{t(`pay_${v}`)}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>

      <div className="erp-card overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center gap-3">
          <div>
            <h3 className="font-heading font-semibold">{t("line_items")}</h3>
            {isFinancial && <p className="text-xs text-slate-500 mt-0.5" data-testid="account-required-hint">{t(isBuy ? "account_required_hint" : "account_required_hint_sales")}</p>}
          </div>
          <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="add-line-item-button">
            <Plus size={14} className="mr-1" /> {t("add_line")}
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm erp-table">
            <thead>
              <tr className="text-left">
                <th className="px-4 py-2 min-w-[240px]">{t("description")}</th>
                <th className="px-2 py-2 min-w-[260px]">{t("account")}</th>
                <th className="px-2 py-2 w-24">{t("quantity")}</th>
                <th className="px-2 py-2 w-32">{t("unit_price")}</th>
                <th className="px-2 py-2 w-28">{t("vat_rate")}</th>
                {isBuy && <th className="px-2 py-2 w-24 text-center">{t("vat_deductible")}</th>}
                <th className="px-4 py-2 w-32 text-right">{t("line_total")}</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {form.items.map((it, i) => (
                <tr key={i} data-testid={`line-item-row-${i}`}>
                  <td className="px-4 py-2"><Input value={it.description} onChange={setItem(i, "description")} data-testid={`line-item-description-${i}`} /></td>
                  <td className="px-2 py-2"><AccountPicker value={it.account_code} onChange={setItem(i, "account_code")} accounts={accounts} testId={`line-item-account-${i}`} /></td>
                  <td className="px-2 py-2"><Input type="number" step="any" min="0" value={it.quantity} onChange={setItem(i, "quantity")} className="font-mono" data-testid={`line-item-quantity-${i}`} /></td>
                  <td className="px-2 py-2"><Input type="number" step="0.01" min="0" value={it.unit_price} onChange={setItem(i, "unit_price")} className="font-mono" data-testid={`line-item-price-${i}`} /></td>
                  <td className="px-2 py-2">
                    <div className="relative">
                      <Input type="number" step="0.1" min="0" max="100" list="vat-rate-presets" value={it.vat_rate} onChange={setItem(i, "vat_rate")}
                        className="font-mono pr-6" data-testid={`line-item-vat-${i}`} />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                    </div>
                  </td>
                  {isBuy && (
                    <td className="px-2 py-2 text-center">
                      <Checkbox checked={it.vat_deductible !== false} onCheckedChange={(v) => setItem(i, "vat_deductible")(!!v)} data-testid={`line-item-vat-deductible-${i}`} />
                    </td>
                  )}
                  <td className="px-4 py-2 text-right font-mono" data-testid={`line-item-total-${i}`}>{fmtMoney((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}</td>
                  <td className="px-2 py-2">
                    <button type="button" onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }))} data-testid={`line-item-remove-${i}`}
                      className="h-8 w-8 inline-flex items-center justify-center rounded text-rose-600 hover:bg-rose-100 dark:hover:bg-rose-950" disabled={form.items.length === 1}>
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <datalist id="vat-rate-presets">{VAT_RATES.map((r) => <option key={r} value={r} />)}</datalist>
        </div>
        {isBuy && <p className="px-5 py-2 text-xs text-slate-500 border-t border-slate-100 dark:border-slate-800">{t("vat_deductible_hint")}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label={t("notes")}>
          <Textarea rows={4} value={form.notes} onChange={set("notes")} data-testid="document-notes-input" />
        </Field>
        <div className="erp-card p-5 space-y-2 font-mono text-sm" data-testid="document-totals">
          <div className="flex justify-between"><span className="font-body text-slate-500">{t("subtotal")}</span><span data-testid="totals-subtotal">{fmtMoney(totals.subtotal)}</span></div>
          {Object.entries(totals.breakdown).filter(([, v]) => v > 0).map(([rate, v]) => (
            <div key={rate} className="flex justify-between text-slate-600 dark:text-slate-300"><span className="font-body">{t("vat_amount")} {rate}%</span><span>{fmtMoney(v)}</span></div>
          ))}
          <div className="flex justify-between border-t border-slate-200 dark:border-slate-700 pt-2 text-lg font-medium">
            <span className="font-body">{t("total_amount")}</span><span data-testid="totals-grand-total">{fmtMoney(totals.total)}</span>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="document-cancel-button">{t("cancel")}</Button>
        <Button type="submit" disabled={saving} className="bg-blue-800 hover:bg-blue-700" data-testid="document-submit-button">{t("save")}</Button>
      </div>
    </form>
  );
}
